import React, { useState } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { RadioButton } from "primereact/radiobutton";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import { InputNumber } from "primereact/inputnumber";
import "./index.scss";

const RemittanceApproval = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [approvalAction, setApprovalAction] = useState("");
  const [comments, setComments] = useState("");
  const [showDelegationDialog, setShowDelegationDialog] = useState(false);
  const [delegationData, setDelegationData] = useState({
    delegateTo: "",
    fromDate: null,
    toDate: null,
    transTypes: ["All"],
    amountLimit: null,
    reason: ""
  });

  const pendingApprovals = [
    {
      id: 1,
      priority: "Urgent",
      referenceNo: "TRF20250926001",
      transactionType: "Electronic Transfer",
      initiator: "John Smith",
      submissionDate: "2025-09-26 10:30",
      amount: 125000,
      description: "Monthly premium settlement to ABC Insurance",
      slaHours: 4,
      currentLevel: 2
    },
    {
      id: 2,
      priority: "High",
      referenceNo: "BLK20250926002",
      transactionType: "Bulk Processing",
      initiator: "Sarah Johnson",
      submissionDate: "2025-09-26 09:15",
      amount: 87500,
      description: "Batch commission processing - September 2025",
      slaHours: 12,
      currentLevel: 1
    },
    {
      id: 3,
      priority: "Normal",
      referenceNo: "SET20250925003",
      transactionType: "Settlement",
      initiator: "Mike Wilson",
      submissionDate: "2025-09-25 14:45",
      amount: 45300,
      description: "Quarterly settlement adjustment",
      slaHours: 24,
      currentLevel: 1
    },
    {
      id: 4,
      priority: "Low",
      referenceNo: "ADJ20250925004",
      transactionType: "Adjustment",
      initiator: "Emily Brown",
      submissionDate: "2025-09-25 11:20",
      amount: 12750,
      description: "Commission adjustment for policy corrections",
      slaHours: 48,
      currentLevel: 1
    }
  ];

  const approvalHistory = [
    {
      referenceNo: "TRF20250920001",
      transactionType: "Electronic Transfer",
      amount: 156000,
      action: "Approved",
      actionDate: "2025-09-20 15:30",
      remarks: "Verified and approved as per guidelines"
    },
    {
      referenceNo: "SET20250919002",
      transactionType: "Settlement",
      amount: 67800,
      action: "Rejected",
      actionDate: "2025-09-19 10:15",
      remarks: "Missing supporting documents"
    },
    {
      referenceNo: "BLK20250918003",
      transactionType: "Bulk Processing",
      amount: 234500,
      action: "Delegated",
      actionDate: "2025-09-18 16:45",
      remarks: "Delegated to Finance Head for review"
    }
  ];

  const activeDelegations = [
    {
      delegatedTo: "Robert Chen",
      fromDate: "2025-09-25",
      toDate: "2025-09-30",
      scope: "All Transactions",
      status: "Active"
    },
    {
      delegatedTo: "Lisa Anderson",
      fromDate: "2025-09-20",
      toDate: "2025-09-24",
      scope: "Settlements Only",
      status: "Expired"
    }
  ];

  const transactionTypeOptions = [
    { label: "All", value: "All" },
    { label: "Settlement", value: "Settlement" },
    { label: "Electronic Transfer", value: "Electronic Transfer" },
    { label: "Bulk Processing", value: "Bulk Processing" },
    { label: "Adjustment", value: "Adjustment" }
  ];

  const priorityOptions = [
    { label: "All", value: "All" },
    { label: "Urgent", value: "Urgent" },
    { label: "High", value: "High" },
    { label: "Normal", value: "Normal" },
    { label: "Low", value: "Low" }
  ];

  const userOptions = [
    { label: "Robert Chen", value: "Robert Chen" },
    { label: "Lisa Anderson", value: "Lisa Anderson" },
    { label: "David Miller", value: "David Miller" },
    { label: "Jennifer Davis", value: "Jennifer Davis" }
  ];

  const priorityBodyTemplate = (rowData) => {
    const getSeverity = (priority) => {
      switch (priority) {
        case 'Urgent': return 'danger';
        case 'High': return 'warning';
        case 'Normal': return 'info';
        case 'Low': return 'success';
        default: return null;
      }
    };
    return <Tag value={rowData.priority} severity={getSeverity(rowData.priority)} />;
  };

  const slaBodyTemplate = (rowData) => {
    const severity = rowData.slaHours < 8 ? 'danger' : rowData.slaHours < 24 ? 'warning' : 'success';
    return <Tag value={`${rowData.slaHours}h remaining`} severity={severity} />;
  };

  const actionBodyTemplate = (rowData) => {
    const getSeverity = (action) => {
      switch (action) {
        case 'Approved': return 'success';
        case 'Rejected': return 'danger';
        case 'Delegated': return 'info';
        default: return null;
      }
    };
    return <Tag value={rowData.action} severity={getSeverity(rowData.action)} />;
  };

  const statusBodyTemplate = (rowData) => {
    const getSeverity = (status) => {
      return status === 'Active' ? 'success' : 'secondary';
    };
    return <Tag value={rowData.status} severity={getSeverity(rowData.status)} />;
  };

  const actionsBodyTemplate = (rowData) => {
    return (
      <div className="action-buttons">
        <Button
          icon="pi pi-eye"
          className="p-button-rounded p-button-text"
          tooltip="View"
          onClick={() => {
            setSelectedTransaction(rowData);
            setShowDetailDialog(true);
          }}
        />
        <Button
          icon="pi pi-check"
          className="p-button-rounded p-button-success p-button-text"
          tooltip="Approve"
        />
        <Button
          icon="pi pi-times"
          className="p-button-rounded p-button-danger p-button-text"
          tooltip="Reject"
        />
      </div>
    );
  };

  const delegationActionsTemplate = (rowData) => {
    return (
      <div className="action-buttons">
        <Button label="Revoke" className="p-button-text p-button-danger p-button-sm" />
        <Button label="Extend" className="p-button-text p-button-sm" />
      </div>
    );
  };

  const detailDialogFooter = (
    <div>
      <Button label="Cancel" icon="pi pi-times" onClick={() => setShowDetailDialog(false)} className="p-button-text" />
      <Button label="Submit" icon="pi pi-check" onClick={() => setShowDetailDialog(false)} autoFocus />
    </div>
  );

  const delegationDialogFooter = (
    <div>
      <Button label="Cancel" icon="pi pi-times" onClick={() => setShowDelegationDialog(false)} className="p-button-text" />
      <Button label="Add Delegation" icon="pi pi-check" onClick={() => setShowDelegationDialog(false)} autoFocus />
    </div>
  );

  return (
    <div className="remittance-approval">
      <h2>Remittance Approval Workflow</h2>

      <div className="summary-cards">
        <Card className="summary-card">
          <div className="card-content">
            <div className="card-icon orange">
              <i className="pi pi-clock" />
            </div>
            <div className="card-details">
              <div className="card-value">8</div>
              <div className="card-label">Pending My Approval</div>
            </div>
          </div>
        </Card>
        <Card className="summary-card">
          <div className="card-content">
            <div className="card-icon blue">
              <i className="pi pi-hourglass" />
            </div>
            <div className="card-details">
              <div className="card-value">15</div>
              <div className="card-label">Total Pending</div>
            </div>
          </div>
        </Card>
        <Card className="summary-card">
          <div className="card-content">
            <div className="card-icon red">
              <i className="pi pi-exclamation-triangle" />
            </div>
            <div className="card-details">
              <div className="card-value">3</div>
              <div className="card-label">Overdue</div>
            </div>
          </div>
        </Card>
        <Card className="summary-card">
          <div className="card-content">
            <div className="card-icon green">
              <i className="pi pi-check" />
            </div>
            <div className="card-details">
              <div className="card-value">12</div>
              <div className="card-label">Approved Today</div>
            </div>
          </div>
        </Card>
      </div>

      <Card className="mt-4">
        <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
          <TabPanel header={<span>Pending Approvals <span className="badge">8</span></span>}>
            <div className="filter-section mb-3">
              <Dropdown placeholder="Transaction Type" options={transactionTypeOptions} className="mr-2" />
              <Dropdown placeholder="Priority" options={priorityOptions} className="mr-2" />
              <Dropdown placeholder="Age" options={[
                { label: "All", value: "All" },
                { label: "< 1 Day", value: "1" },
                { label: "1-3 Days", value: "3" },
                { label: "3-7 Days", value: "7" },
                { label: "> 7 Days", value: "7+" }
              ]} className="mr-2" />
              <Button label="Filter" icon="pi pi-filter" className="p-button-primary mr-2" />
              <Button label="Clear" icon="pi pi-times" className="p-button-secondary" />
            </div>

            <DataTable
              value={pendingApprovals}
              selection={selectedRows}
              onSelectionChange={(e) => setSelectedRows(e.value)}
              dataKey="id"
              stripedRows
            >
              <Column selectionMode="multiple" style={{ width: '3rem' }} />
              <Column field="priority" header="" body={priorityBodyTemplate} style={{ width: '5rem' }} />
              <Column field="referenceNo" header="Reference No" />
              <Column field="transactionType" header="Type" />
              <Column field="initiator" header="Initiated By" />
              <Column field="submissionDate" header="Submitted" />
              <Column field="amount" header="Amount" body={(data) => `$${data.amount.toLocaleString()}`} />
              <Column field="description" header="Description" />
              <Column field="slaHours" header="SLA" body={slaBodyTemplate} />
              <Column field="currentLevel" header="Level" />
              <Column header="Actions" body={actionsBodyTemplate} style={{ width: '150px' }} />
            </DataTable>

            {selectedRows.length > 0 && (
              <div className="bulk-actions mt-3">
                <Button label="Bulk Approve" icon="pi pi-check" className="p-button-success mr-2" />
                <Button label="Bulk Reject" icon="pi pi-times" className="p-button-danger mr-2" />
                <Button label="Delegate" icon="pi pi-forward" className="p-button-secondary" />
              </div>
            )}
          </TabPanel>

          <TabPanel header="Approval History">
            <div className="filter-section mb-3">
              <Calendar placeholder="Date Range" selectionMode="range" className="mr-2" />
              <Dropdown placeholder="Action" options={[
                { label: "All", value: "All" },
                { label: "Approved", value: "Approved" },
                { label: "Rejected", value: "Rejected" },
                { label: "Delegated", value: "Delegated" }
              ]} className="mr-2" />
              <Button label="Search" icon="pi pi-search" />
            </div>

            <DataTable value={approvalHistory} stripedRows>
              <Column field="referenceNo" header="Reference No" />
              <Column field="transactionType" header="Type" />
              <Column field="amount" header="Amount" body={(data) => `$${data.amount.toLocaleString()}`} />
              <Column field="action" header="Action" body={actionBodyTemplate} />
              <Column field="actionDate" header="Action Date" />
              <Column field="remarks" header="Remarks" />
            </DataTable>
          </TabPanel>

          <TabPanel header="Delegation">
            <div className="delegation-section">
              <div className="section-header mb-3">
                <h3>Active Delegations</h3>
                <Button
                  label="Add New Delegation"
                  icon="pi pi-plus"
                  onClick={() => setShowDelegationDialog(true)}
                />
              </div>

              <DataTable value={activeDelegations} stripedRows>
                <Column field="delegatedTo" header="Delegated To" />
                <Column field="fromDate" header="From Date" />
                <Column field="toDate" header="To Date" />
                <Column field="scope" header="Scope" />
                <Column field="status" header="Status" body={statusBodyTemplate} />
                <Column header="Actions" body={delegationActionsTemplate} />
              </DataTable>
            </div>
          </TabPanel>
        </TabView>
      </Card>

      <Dialog
        header="Approval Details"
        visible={showDetailDialog}
        style={{ width: '60vw' }}
        footer={detailDialogFooter}
        onHide={() => setShowDetailDialog(false)}
      >
        {selectedTransaction && (
          <div className="approval-details">
            <div className="detail-section">
              <h4>Transaction Information</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Reference No:</label>
                  <span>{selectedTransaction.referenceNo}</span>
                </div>
                <div className="detail-item">
                  <label>Type:</label>
                  <span>{selectedTransaction.transactionType}</span>
                </div>
                <div className="detail-item">
                  <label>Amount:</label>
                  <span>${selectedTransaction.amount.toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Initiated By:</label>
                  <span>{selectedTransaction.initiator}</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Approval Action</h4>
              <div className="approval-action">
                <div className="p-field-radiobutton mb-2">
                  <RadioButton
                    inputId="approve"
                    value="approve"
                    onChange={(e) => setApprovalAction(e.value)}
                    checked={approvalAction === 'approve'}
                  />
                  <label htmlFor="approve" className="ml-2">Approve</label>
                </div>
                <div className="p-field-radiobutton mb-2">
                  <RadioButton
                    inputId="reject"
                    value="reject"
                    onChange={(e) => setApprovalAction(e.value)}
                    checked={approvalAction === 'reject'}
                  />
                  <label htmlFor="reject" className="ml-2">Reject</label>
                </div>
                <div className="p-field-radiobutton">
                  <RadioButton
                    inputId="hold"
                    value="hold"
                    onChange={(e) => setApprovalAction(e.value)}
                    checked={approvalAction === 'hold'}
                  />
                  <label htmlFor="hold" className="ml-2">Hold for Review</label>
                </div>
              </div>

              {(approvalAction === 'reject' || approvalAction === 'hold') && (
                <div className="mt-3">
                  <label>Comments *</label>
                  <InputTextarea
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    rows={3}
                    className="w-full"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        header="Add New Delegation"
        visible={showDelegationDialog}
        style={{ width: '50vw' }}
        footer={delegationDialogFooter}
        onHide={() => setShowDelegationDialog(false)}
      >
        <div className="delegation-form">
          <div className="p-fluid p-formgrid p-grid">
            <div className="p-field p-col-12 p-md-6">
              <label>Delegate To *</label>
              <Dropdown
                value={delegationData.delegateTo}
                options={userOptions}
                onChange={(e) => setDelegationData({ ...delegationData, delegateTo: e.value })}
                placeholder="Select User"
              />
            </div>
            <div className="p-field p-col-12 p-md-6">
              <label>Delegation Period *</label>
              <div className="date-range">
                <Calendar
                  value={delegationData.fromDate}
                  onChange={(e) => setDelegationData({ ...delegationData, fromDate: e.value })}
                  placeholder="From Date"
                  className="mr-2"
                />
                <Calendar
                  value={delegationData.toDate}
                  onChange={(e) => setDelegationData({ ...delegationData, toDate: e.value })}
                  placeholder="To Date"
                />
              </div>
            </div>
            <div className="p-field p-col-12 p-md-6">
              <label>Transaction Types</label>
              <MultiSelect
                value={delegationData.transTypes}
                options={transactionTypeOptions}
                onChange={(e) => setDelegationData({ ...delegationData, transTypes: e.value })}
                display="chip"
              />
            </div>
            <div className="p-field p-col-12 p-md-6">
              <label>Amount Limit</label>
              <InputNumber
                value={delegationData.amountLimit}
                onValueChange={(e) => setDelegationData({ ...delegationData, amountLimit: e.value })}
                mode="currency"
                currency="USD"
              />
            </div>
            <div className="p-field p-col-12">
              <label>Reason *</label>
              <InputTextarea
                value={delegationData.reason}
                onChange={(e) => setDelegationData({ ...delegationData, reason: e.target.value })}
                rows={2}
              />
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default RemittanceApproval;